import { log } from '@/log'
import { ApiCreditcard } from '@/models/api/creditcard'
import { CreditcardRepository, RegisterContext } from '@/models/payment/creditcard/repository/creditcard-repository'
import { CreditcardFormState } from '@spa/components/organisms/payment/creditcard/creditcardFormState'
import { User } from '@spa/store/modules/user/types'
import { none } from 'fp-ts/lib/Option'
import { ActionExecutor, ActionRequestList, AtMostOneRedirectWithHtmlRule } from '../action/action'

/**
 * クレジットカードの新規登録で必要なオブジェクトの構成.
 */
export interface CreditcardRegistrationDependencies {
  readonly repository: {
    /**
     * ユーザーが主に登録しようとしているクレジットカードのリポジトリ.
     *
     * 具体的な構成例は {@link purchaseContext} および {@link subscriptionContext} を参照.
     */
    readonly main: RepositoryDependency
    /**
     * ユーザーが主に登録しようとしているクレジットカードとは別のクレジットカード用リポジトリ.
     *
     * 具体的な構成例は {@link purchaseContext} および {@link subscriptionContext} を参照.
     */
    readonly sub: RepositoryDependency
  }
}
export interface RepositoryDependency {
  readonly register: CreditcardRepository['register']

  getRegistered(): Promise<ApiCreditcard[]>
}

/**
 * このメソッドはsubscription文脈・purchase文脈に関して透過的である。それどころかmain/sub両方のリポジトリを呼ぶ場合、二つの文脈にまたがることも想定される。
 * 一方、引数contextは、文脈に応じてsomeを渡すべきかnoneを渡すべきか変わりうる。
 *   例: subscription文脈では3Dセキュアを利用するという暗黙的な依存関係がある。
 * 本来であれば文脈に応じてメソッド自体わけるかジェネリクスなどを用いて、必要な引数についてもっと厳格な型を宣言するべきだが、
 * 元々透過的な作りになっているところに増改修を加えたという経緯から、このような形になっている。
 * 適切な型制約が付されるまでは、このメソッドの利用者は引数contextについて、自らの責任で適切な内容を渡す必要がある。
 */
export async function registerNewCreditcard(
  { repository }: CreditcardRegistrationDependencies,
  creditcard: CreditcardFormState,
  user: User,
  context: RegisterUseCaseContext = registerWithout3DSecureOnlyForMainCardContext
): Promise<void> {
  const { enableSubProcess, ...registerContext } = context

  if (enableSubProcess) {
    return registerNewCreditcardForMainAndSub(
      { repository },
      creditcard,
      user,
      registerContext
    )
  }

  return registerNewCreditcardOnlyForMain(
    { repository },
    creditcard,
    user,
    registerContext
  )
}

async function registerNewCreditcardOnlyForMain(
  { repository }: CreditcardRegistrationDependencies,
  creditcard: CreditcardFormState,
  user: User,
  context: RegisterContext
): Promise<void> {
  const result = new ActionRequestList([
    await repository.main.register(creditcard, user, context),
  ])

  await Promise.all(
    new ActionExecutor(new AtMostOneRedirectWithHtmlRule()).execute(result)
  )

  return
}

async function registerNewCreditcardForMainAndSub(
  { repository }: CreditcardRegistrationDependencies,
  creditcard: CreditcardFormState,
  user: User,
  context: RegisterContext
): Promise<void> {
  const [cardsForMain, cardsForSub] = await Promise.all([
    repository.main.getRegistered(),
    repository.sub.getRegistered(),
  ])

  /*
   * https://github.com/my-color/front/issues/6291 では
   * 「初回の登録時に、通常の購入用カードとサブスクリプション用のカードを両方登録して、手間を減らしたい」
   * が解決したい問題.
   *
   * 2022/05/04時点では、クレジットカードの登録・削除履歴などをEngineから取得する手段が特に無いため、
   * 「クレジットカードが通常購入用/サブスクリプション用併せて0枚」を以て「初回」の代替としている.
   *
   * main/subの扱いについて、例えば「mainが登録されていても、subが0ならsubも登録する」という方針もあり得るかもしれない.
   * しかし、2022/05/04時点では、クレジットカードの取り扱いについて「最初の登録を簡略化する」というレベルで話がとどまっており、
   * 削除等を挟んだ後のカード管理をどうするかは特に規定されていない.
   * 一旦、実装や条件が単純な「0件なら両方登録、それ以外ならmainのみ登録」にしているが、
   * 今後の検討によっては、条件が細分化する可能性もある.
   */
  if ([...cardsForMain, ...cardsForSub].length !== 0) {
    return registerNewCreditcardOnlyForMain(
      { repository },
      creditcard,
      user,
      context
    )
  }

  log.info([
    'this is first credit card registration.',
    'so register creditcards for both of purchase and subscription',
    'to reduce for the user the effort of inputting creditcard information',
  ].join(' '))

  const results = new ActionRequestList([
    await repository.main.register(creditcard, user, context),
    await repository.sub.register(creditcard, user, context),
  ])

  /**
   * mainとsubの両方を登録する場合、高々どちらか片方だけが3Dセキュアという強い前提を置いている。
   * 3Dセキュアでは画面遷移が必要である以上、そして一般的に二回連続して画面遷移してもエンドユーザに混乱を招く以上、
   * この前提自体は十分に妥当と考えられる。
   * むしろ、この前提を型で表明するか、そもそもmainとsubの両方の自動登録をやめる（あるいはserverの責務に移す）ことを検討する。
   */
  await Promise.all(
    new ActionExecutor(new AtMostOneRedirectWithHtmlRule()).execute(results)
  )

  return
}

export type  RegisterUseCaseContext = RegisterContext & {
  /**
   * UI上ユーザに提示されるカード登録を「メイン」のプロセスとすれば、その裏側で自動的に別の（関連のある）カード登録を「サブ」プロセスとして走らせる機能があり、その有効化フラグ。
   * 例えば、「メイン」としてサブスクリプション用の3Dセキュアクレジットカード登録を行う場合、「サブ」プロセスで自動的に通常購入用のクレジットカードも登録する等。
   * 3Dセキュアとそうでないカード登録では、必要なフォームの内容や画面遷移などユーザに露出するUI/UXが異なるため、場合によってはサブプロセスを有効化しない方が良い場合もある。
   */
  readonly enableSubProcess: boolean
}

export const registerWithout3DSecureOnlyForMainCardContext: RegisterUseCaseContext = {
  enableSubProcess: false,
  threeDSecureRedirectUrl: none,
  threeDSecureExtraProfile: none,
}
